import { Injectable, signal, computed } from '@angular/core';

export type MessageType = 'success' | 'info' | 'warning' | 'danger';

export interface MessageOptions {
    content: string;
    type?: MessageType;
    duration?: number;
    dismissible?: boolean;
    max?: number;
}

export interface MessageRef {
    id: number;
    close: () => void;
}

interface Message {
    id: number;
    content: string;
    type: MessageType;
    dismissible: boolean;
}

@Injectable({ providedIn: 'root' })
export class MessageService {
    private _messages = signal<Message[]>([]);
    private seq = 0;
    private timers = new Map<number, any>();

    readonly messages = computed(() => this._messages());

    open(opts: MessageOptions): MessageRef {
        const id = ++this.seq;
        const duration = opts.duration ?? 5000;
        const model: Message = {
            id,
            content: opts.content,
            type: opts.type ?? 'info',
            dismissible: opts.dismissible ?? true,
        };
        this._messages.update(arr => [...arr, model]);
        if (duration > 0) {
            const t = setTimeout(() => this.close(id), duration);
            this.timers.set(id, t);
        }
        return { id, close: () => this.close(id) };
    }

    success(content: string, duration?: number) {
        return this.open({ content, type: 'success', duration });
    }

    info(content: string, duration?: number) {
        return this.open({ content, type: 'info', duration });
    }

    warning(content: string, duration?: number) {
        return this.open({ content, type: 'warning', duration });
    }

    error(content: string, duration?: number) {
        return this.open({ content, type: 'danger', duration });
    }

    close(id: number): void {
        const t = this.timers.get(id);
        if (t) { clearTimeout(t); this.timers.delete(id); }
        this._messages.update(arr => arr.filter(m => m.id !== id));
    }

    clearAll(): void {
        this.timers.forEach(t => clearTimeout(t));
        this.timers.clear();
        this._messages.set([]);
    }
}
